import { t } from 'i18next';
import { useCallback, useMemo } from 'react';
import { Flow } from '@thive/fddm-types';

import { logger } from '@common/logging/logger';
import { assertNever } from '@/helpers/utils';
import { createProcessRelatedFlowsMap } from '@/modules/flowsV2/helpers';
import {
  FlowLoadHandlingType,
  getFirstFlowStopArea,
  getFlowDataFromElement,
  getFlowRuleAreaId,
  getFlowStopAttachPoint,
  getFlowStopLoadHandlingType,
  isFlowTypeAllowedForEndpoint,
  isShuttleFlowEndpoint,
} from '@/modules/flowsV2/flowEndpoint';
import { FlowStopType, useActiveFlowStore } from '@/modules/flowsV2/store/useActiveFlowStore';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { useSnackbarStore } from '@/modules/snackbar/store/useSnackbarStore';
import { useAppStore } from '@/modules/app/store/useAppStore';

import { getVehicleTypesByArea } from '../helpers/vehicleType';

type FlowDirection = 'inbound' | 'outbound';

const PROCESS_LOAD_HANDLING_TYPES: FlowLoadHandlingType[] = [
  'OFF_VEHICLE_PROCESSING',
  'ON_VEHICLE_PROCESSING',
];

export const useFlowsCallbacks = () => {
  const { showSnackbar } = useSnackbarStore();
  const { getAreaById, getFlowStopById } = useFloorplanDesignStore.getState();

  const getVehicleTypesForFlowStop = useCallback(
    (flowStopType: FlowStopType, id: string): string[] | undefined => {
      if (flowStopType === 'area') {
        const area = getAreaById(id);

        if (!area) {
          logger.error(`Area with id ${id} not found`);
          return undefined;
        }

        return getVehicleTypesByArea(area);
      }

      if (flowStopType === 'flowStop') {
        const area = getFirstFlowStopArea(id);

        return area ? getVehicleTypesByArea(area) : undefined;
      }

      assertNever(flowStopType);
    },
    [getAreaById],
  );

  const areVehicleTypesValid = useCallback(
    (
      flowType: Flow['type'],
      sourceFlowStopType: FlowStopType,
      sourceId: string,
      targetFlowStopType: FlowStopType,
      targetId: string,
    ) => {
      const isSourceAllowed = isFlowTypeAllowedForEndpoint(flowType, sourceFlowStopType, sourceId);
      const isTargetAllowed = isFlowTypeAllowedForEndpoint(flowType, targetFlowStopType, targetId);

      if (!isSourceAllowed || !isTargetAllowed) return false;

      const hasShuttleEndpoint =
        isShuttleFlowEndpoint(sourceFlowStopType, sourceId) ||
        isShuttleFlowEndpoint(targetFlowStopType, targetId);

      if (
        hasShuttleEndpoint &&
        (flowType === 'CONSOLIDATION_STORAGE' || flowType === 'CONSOLIDATION_RETRIEVAL')
      ) {
        return true;
      }

      const sourceVehicleTypes = getVehicleTypesForFlowStop(sourceFlowStopType, sourceId);
      const targetVehicleTypes = getVehicleTypesForFlowStop(targetFlowStopType, targetId);

      if (!sourceVehicleTypes?.length || !targetVehicleTypes?.length) return false;

      return sourceVehicleTypes.some((vehicleType) => targetVehicleTypes.includes(vehicleType));
    },
    [getVehicleTypesForFlowStop],
  );

  const hasProcessFlowLimitReached = useCallback((id: string, direction: FlowDirection) => {
    const { flows } = useFloorplanDesignStore.getState().design.flows;
    const relatedFlowsMap = createProcessRelatedFlowsMap([id], flows);
    const relatedFlows = relatedFlowsMap.get(id);

    return Boolean(relatedFlows?.[direction].length);
  }, []);

  const getFlowsForRuleArea = useCallback((id: string) => {
    const { getFlowsByAreaId } = useFloorplanDesignStore.getState();
    const ruleAreaId = getFlowRuleAreaId(id);

    return {
      ruleAreaId,
      flows: getFlowsByAreaId(ruleAreaId),
    };
  }, []);

  const hasExternalFlow = useCallback(
    (id: string) => {
      const { flows } = getFlowsForRuleArea(id);

      return flows.some((flow) => flow.type === 'EXTERNAL');
    },
    [getFlowsForRuleArea],
  );

  const hasMixedExternalAndInternalFlows = useCallback(
    (id: string) => {
      const { flows } = getFlowsForRuleArea(id);
      const hasExternalFlowForArea = flows.some((flow) => flow.type === 'EXTERNAL');
      const hasInternalFlowForArea = flows.some((flow) => flow.type !== 'EXTERNAL');

      return hasExternalFlowForArea && hasInternalFlowForArea;
    },
    [getFlowsForRuleArea],
  );

  const hasOneInternalFlowInDirection = useCallback(
    (id: string, direction: FlowDirection) => {
      const { ruleAreaId, flows } = getFlowsForRuleArea(id);
      const internalFlows = flows.filter((flow) => flow.type !== 'EXTERNAL');

      if (internalFlows.length !== 1) return false;

      const [internalFlow] = internalFlows;
      let connectedEndpointId = internalFlow.sourceAreaOrFlowStopId;

      if (direction === 'inbound') {
        connectedEndpointId = internalFlow.destinationAreaOrFlowStopId;
      }

      return getFlowRuleAreaId(connectedEndpointId) === ruleAreaId;
    },
    [getFlowsForRuleArea],
  );

  const hasDuplicateFlow = useCallback((sourceId: string, targetId: string) => {
    const { flows } = useFloorplanDesignStore.getState().design.flows;
    const sourceRuleAreaId = getFlowRuleAreaId(sourceId);
    const targetRuleAreaId = getFlowRuleAreaId(targetId);

    return flows.some(
      (flow) =>
        getFlowRuleAreaId(flow.sourceAreaOrFlowStopId) === sourceRuleAreaId &&
        getFlowRuleAreaId(flow.destinationAreaOrFlowStopId) === targetRuleAreaId,
    );
  }, []);

  const isValidSource = useCallback(
    (id: string, flowStopType: FlowStopType, loadHandlingType: FlowLoadHandlingType) => {
      const { selectedToolId } = useAppStore.getState();
      const isExternalFlowTool = selectedToolId === 'external_flow';
      const isProcessArea =
        flowStopType === 'area' && PROCESS_LOAD_HANDLING_TYPES.includes(loadHandlingType);

      if (isProcessArea && hasProcessFlowLimitReached(id, 'outbound')) {
        showSnackbar(t('errors:flow.process_flow_limit_reached'));
        return false;
      }

      if (isExternalFlowTool && loadHandlingType === 'DELIVERY' && hasExternalFlow(id)) {
        showSnackbar(t('errors:flow.external_flow_limit_reached'));
        return false;
      }

      if (
        isExternalFlowTool &&
        loadHandlingType === 'DELIVERY' &&
        !hasOneInternalFlowInDirection(id, 'inbound')
      ) {
        showSnackbar(t('errors:flow.external_flow_preceding_limit_reached'));
        return false;
      }

      if (
        isExternalFlowTool &&
        loadHandlingType === 'INTAKE' &&
        hasMixedExternalAndInternalFlows(id)
      ) {
        showSnackbar(t('errors:flow.external_flow_following_limit_reached'));
        return false;
      }

      return true;
    },
    [
      hasExternalFlow,
      hasMixedExternalAndInternalFlows,
      hasOneInternalFlowInDirection,
      hasProcessFlowLimitReached,
      showSnackbar,
    ],
  );

  const isValidTarget = useCallback(
    (id: string, flowStopType: FlowStopType, targetLoadHandlingType: FlowLoadHandlingType) => {
      const { selectedToolId } = useAppStore.getState();
      const isExternalFlowTool = selectedToolId === 'external_flow';
      const { currentFlowSourceId, currentFlowSourceFlowStopType, currentFlowType } =
        useActiveFlowStore.getState();

      if (!currentFlowSourceId || !currentFlowSourceFlowStopType || !currentFlowType) return false;

      if (!getFlowStopById(id) && !getAreaById(id)) {
        logger.error(`Could not find target with id ${id}`);
        return false;
      }

      if (hasDuplicateFlow(currentFlowSourceId, id)) {
        showSnackbar(t('errors:flow.duplicate_flow.drawing'));
        return false;
      }

      const hasValidVehicleTypes = areVehicleTypesValid(
        currentFlowType,
        currentFlowSourceFlowStopType,
        currentFlowSourceId,
        flowStopType,
        id,
      );

      if (!hasValidVehicleTypes) {
        showSnackbar(t('errors:flow.different_vehicle_types'));
        return false;
      }

      const isProcessArea =
        flowStopType === 'area' && PROCESS_LOAD_HANDLING_TYPES.includes(targetLoadHandlingType);

      if (isProcessArea && hasProcessFlowLimitReached(id, 'inbound')) {
        showSnackbar(t('errors:flow.process_flow_limit_reached'));
        return false;
      }

      if (isExternalFlowTool && targetLoadHandlingType === 'INTAKE' && hasExternalFlow(id)) {
        showSnackbar(t('errors:flow.external_flow_limit_reached'));
        return false;
      }

      if (
        isExternalFlowTool &&
        targetLoadHandlingType === 'INTAKE' &&
        !hasOneInternalFlowInDirection(id, 'outbound')
      ) {
        showSnackbar(t('errors:flow.external_flow_following_limit_reached'));
        return false;
      }

      if (
        isExternalFlowTool &&
        targetLoadHandlingType === 'DELIVERY' &&
        hasMixedExternalAndInternalFlows(id)
      ) {
        showSnackbar(t('errors:flow.external_flow_preceding_limit_reached'));
        return false;
      }

      return true;
    },
    [
      areVehicleTypesValid,
      getAreaById,
      getFlowStopById,
      hasDuplicateFlow,
      hasExternalFlow,
      hasMixedExternalAndInternalFlows,
      hasOneInternalFlowInDirection,
      hasProcessFlowLimitReached,
      showSnackbar,
    ],
  );

  return useMemo(
    () => ({
      getFlowDataFromElement,
      isValidSource,
      isValidTarget,
      getFlowStopLoadHandlingType,
      getFlowStopAttachPoint,
    }),
    [isValidSource, isValidTarget],
  );
};
